/**
 * ReportEditorDialog — Full-screen dialog wrapper for the ReportEditor.
 * Opened from CREATEREPORT / EDITREPORT study actions.
 */
import { useCallback, useState, useEffect, startTransition } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import type { Study, ReportBean } from '../../api/types';
import { ReportEditor } from './ReportEditor';

interface ReportEditorDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete?: () => void;
  /** If 'create', opens editor for new report. If 'edit', shows report list first. */
  mode: 'create' | 'edit';
}

export const ReportEditorDialog = ({
  open,
  study,
  onClose,
  onComplete,
  mode,
}: ReportEditorDialogProps) => {
  const [selectedReport, setSelectedReport] = useState<ReportBean | null>(null);
  const [editing, setEditing] = useState(false);

  // Fetch existing reports when in edit mode
  const {
    data: reportsResult,
    isLoading: reportsLoading,
    refetch,
  } = useQuery({
    queryKey: ['reports', study?.objKey ?? ''],
    queryFn: () => api.getReports(study!.objKey),
    enabled: open && !!study && mode === 'edit',
    staleTime: 10_000,
  });

  const reports = reportsResult?.records ?? [];

  // Auto-start editing for new reports
  useEffect(() => {
    if (open && mode === 'create') {
      startTransition(() => { setSelectedReport(null); setEditing(true); });
    } else if (!open) {
      startTransition(() => { setEditing(false); setSelectedReport(null); });
    }
  }, [open, mode]);

  const handleClose = useCallback(() => {
    setEditing(false);
    setSelectedReport(null);
    onClose();
  }, [onClose]);

  const handleSaved = useCallback(() => {
    refetch();
    onComplete?.();
  }, [refetch, onComplete]);

  const handleSelectReport = (report: ReportBean) => {
    setSelectedReport(report);
    setEditing(true);
  };

  if (!study) return null;

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            {mode === 'create' ? 'New Report' : 'Edit Report'} — {study.name} ({study.acc})
          </Typography>
          <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {editing ? (
          <ReportEditor
            study={study}
            report={selectedReport}
            onClose={() => {
              if (mode === 'edit') {
                setEditing(false);
              } else {
                handleClose();
              }
            }}
            onSaved={handleSaved}
          />
        ) : (
          // Report list for edit mode
          <Box sx={{ p: 2, maxWidth: 800, mx: 'auto', width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select a report to edit
            </Typography>
            {reportsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            )}
            {!reportsLoading && reports.length === 0 && (
              <Alert severity="info">
                No reports found for this study. Use &ldquo;Add Report&rdquo; to create one.
              </Alert>
            )}
            <List>
              {reports.map((report) => (
                <Box key={report.objKey}>
                  <ListItemButton onClick={() => handleSelectReport(report)}>
                    <ListItemText
                      primary={`Report — ${report.status ?? 'UNKNOWN'}`}
                      secondary={[
                        report.authorString && `Author: ${report.authorString}`,
                        report.radiologistString && `Radiologist: ${report.radiologistString}`,
                        report.date && `Date: ${new Date(report.date).toLocaleString()}`,
                        report.addendums?.length
                          ? `${report.addendums.length} addendum(s)`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' | ')}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                  <Divider />
                </Box>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};
